/**
 * CandidateSavedJobs — Wishlist page (V1.6.9).
 *
 * Lists jobs the candidate has hearted from `/jobs/:id`. Empty state
 * points them back to Browse so they don't dead-end here.
 *
 * Rides on the V1.6.7 saved-jobs endpoints:
 *   GET    /api/me/candidate/saved-jobs        → { items, job_ids }
 *   DELETE /api/me/candidate/saved-jobs/{id}   → { ok, removed }
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Heart,
    Briefcase,
    MapPin,
    ArrowRight,
    Trash,
    Lightning,
} from "@phosphor-icons/react";

export default function CandidateSavedJobs() {
    const [saved, setSaved] = useState([]);      // list of {job_id, saved_at, job?}
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/me/candidate/saved-jobs");
            const rows = data?.items || [];
            // Hydrate job details in parallel via public detail endpoint.
            const hydrated = await Promise.all(
                rows.map(async (r) => {
                    try {
                        const j = await api.get(`/public/jobs/${r.job_id}`);
                        return { ...r, job: j.data };
                    } catch {
                        return { ...r, job: null };  // job may be closed / gone
                    }
                }),
            );
            setSaved(hydrated);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load saved jobs");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function unsave(jobId) {
        setBusyId(jobId);
        try {
            await api.delete(`/me/candidate/saved-jobs/${jobId}`);
            setSaved((prev) => prev.filter((r) => r.job_id !== jobId));
            toast.success("Removed from wishlist");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't remove");
        } finally { setBusyId(null); }
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="candidate-saved-jobs-page">
            <header className="flex items-center gap-3 mb-6">
                <Heart size={26} weight="fill" className="text-red-500" />
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">
                        Saved jobs
                    </h1>
                    <p className="text-xs text-ink/50 mt-0.5">
                        Roles you hearted while browsing. Apply when ready.
                    </p>
                </div>
                <span
                    data-testid="saved-jobs-count"
                    className="ml-auto text-[11px] font-black uppercase tracking-wider bg-ink text-white px-3 py-1.5"
                >
                    {loading ? "…" : `${saved.length} saved`}
                </span>
            </header>

            {loading ? (
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-24 bg-white border border-ink/10 animate-pulse" />
                    ))}
                </div>
            ) : saved.length === 0 ? (
                <div
                    data-testid="saved-jobs-empty"
                    className="bg-white border-2 border-dashed border-ink/15 p-10 text-center"
                >
                    <Heart size={44} weight="regular" className="mx-auto text-ink/20 mb-4" />
                    <h2 className="font-heading text-xl font-bold mb-2">
                        No saved jobs yet
                    </h2>
                    <p className="text-sm text-ink/50 mb-5 max-w-md mx-auto">
                        Tap the ♥ Save button on any role to keep it handy for later — you'll find them all here.
                    </p>
                    <Link
                        to="/candidate/jobs"
                        data-testid="saved-jobs-empty-browse"
                        className="btn-gradient inline-flex items-center gap-2 px-5 py-3 text-xs font-black uppercase tracking-wider"
                    >
                        <Briefcase size={13} weight="bold" />
                        Browse roles
                        <ArrowRight size={13} weight="bold" />
                    </Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {saved.map((r) => (
                        <article
                            key={r.job_id}
                            data-testid={`saved-job-row-${r.job_id}`}
                            className="bg-white border-2 border-ink/10 p-4 sm:p-5 hover:border-cobalt transition-colors flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <h3 className="font-heading text-base sm:text-lg font-bold truncate">
                                        {r.job?.title || (
                                            <span className="text-ink/40 italic">
                                                Role no longer available
                                            </span>
                                        )}
                                    </h3>
                                    {r.job?.status && r.job.status !== "Open" && (
                                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 font-black">
                                            {r.job.status}
                                        </span>
                                    )}
                                </div>
                                {r.job?.client?.name && (
                                    <p className="text-xs text-ink/60 mb-1">
                                        {r.job.client.name}
                                    </p>
                                )}
                                <div className="flex items-center gap-3 text-[11px] text-ink/50">
                                    {r.job?.location && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin size={11} /> {r.job.location}
                                        </span>
                                    )}
                                    <span>
                                        Saved {new Date(r.saved_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                                <button
                                    type="button"
                                    onClick={() => unsave(r.job_id)}
                                    disabled={busyId === r.job_id}
                                    data-testid={`saved-job-unsave-${r.job_id}`}
                                    aria-label="Remove from wishlist"
                                    className="border-2 border-ink/15 hover:border-red-300 hover:text-red-600 text-ink/50 p-2.5 transition-colors disabled:opacity-50"
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                                {r.job ? (
                                    <>
                                        <Link
                                            to={`/jobs/${r.job_id}`}
                                            data-testid={`saved-job-view-${r.job_id}`}
                                            className="border-2 border-ink text-ink px-4 py-2.5 text-[11px] font-black uppercase tracking-wider hover:bg-ink hover:text-white transition-colors"
                                        >
                                            View
                                        </Link>
                                        {r.job.status === "Open" && (
                                            <Link
                                                to={`/apply/${r.job_id}`}
                                                data-testid={`saved-job-apply-${r.job_id}`}
                                                className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider"
                                            >
                                                <Lightning size={11} weight="bold" />
                                                Apply
                                            </Link>
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
